import React, { useState, useEffect, useRef } from 'react';
import { api } from '../services/api';
import { useAuth } from '../context/AuthContext';
import useDebouncedValue from '../hooks/useDebouncedValue';

// Searchable employee field backed by the API's own search.
//
// Both callers used to download the directory and filter it in the browser.
// TransferModal paginated all of it, 500 rows at a time, on every open —
// `while (true)` with no page cap. AddAssetModal asked for the first 500 and
// silently dropped everyone after that, so employee 501 simply could not be
// assigned and nothing said so.
//
// GET /api/employees matches name, email and employee number server-side, so
// asking it for 50 hits per keystroke-pause is both cheaper and more complete.
// It does not match home location; that is its own control where it matters.
export default function EmployeePicker({
  value,
  selectedName,
  onChange,
  allowUnassigned = true,
  placeholder = 'Search by name, employee #, or email…',
}) {
  const { withAuth } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounced = useDebouncedValue(search);
  const ref = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    withAuth(() => api.getEmployees({
      limit: 50,
      sort: 'name',
      // Active only. The Workday report now carries leavers as well, so the
      // employees table holds every past employee too — without this, handing
      // someone a laptop would offer a list of people who no longer work here.
      status: 'active',
      ...(debounced ? { search: debounced } : {}),
    }))
      .then(res => { if (!cancelled) setEmployees(res.data || []); })
      .catch(() => { if (!cancelled) setEmployees([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [withAuth, debounced]);

  useEffect(() => {
    function handleClick(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function select(emp) {
    onChange(emp);
    setSearch('');
    setOpen(false);
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {value ? (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '8px 12px', borderRadius: 'var(--radius-sm)',
          border: '1px solid var(--border)', background: 'var(--bg-2)',
          fontSize: 14,
        }}>
          <span>{selectedName}</span>
          <button
            type="button"
            onClick={() => select(null)}
            style={{ background: 'none', border: 'none', color: 'var(--text-2)', cursor: 'pointer', fontSize: 16, padding: '0 4px' }}
          >×</button>
        </div>
      ) : (
        <input
          type="text"
          placeholder={placeholder}
          value={search}
          onChange={e => { setSearch(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          style={{ width: '100%' }}
        />
      )}

      {open && !value && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 1000,
          background: 'var(--bg-1)', border: '1px solid var(--border)',
          borderRadius: 'var(--radius-sm)', maxHeight: 280, overflowY: 'auto',
          boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
        }}>
          {allowUnassigned && (
            <div
              style={{ padding: '8px 12px', fontSize: 14, cursor: 'pointer', color: 'var(--text-2)', borderBottom: '1px solid var(--border)' }}
              onMouseDown={() => select(null)}
            >
              — Unassigned —
            </div>
          )}
          {loading ? (
            <div style={{ padding: '8px 12px', fontSize: 14, color: 'var(--text-2)', display: 'flex', alignItems: 'center', gap: 8 }}>
              <div className="spinner" /> Searching…
            </div>
          ) : employees.length === 0 ? (
            <div style={{ padding: '8px 12px', fontSize: 14, color: 'var(--text-2)' }}>No employees found</div>
          ) : employees.map(emp => (
            <div
              key={emp.id}
              onMouseDown={() => select(emp)}
              style={{
                padding: '8px 12px', fontSize: 14, cursor: 'pointer',
                borderBottom: '1px solid var(--border)',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-2)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <div style={{ fontWeight: 500 }}>{emp.last_name}, {emp.first_name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-2)' }}>
                {emp.job_title}{emp.home_location_name ? ` · ${emp.home_location_name}` : ''}
                {emp.employee_number ? ` · #${emp.employee_number}` : ''}
              </div>
            </div>
          ))}
          {!loading && employees.length === 50 && (
            <div style={{ padding: '6px 12px', fontSize: 12, color: 'var(--text-2)', textAlign: 'center' }}>
              Showing first 50 — type to narrow results
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Convenience for callers that keep a display name alongside the id.
export function employeeName(emp) {
  return emp ? `${emp.first_name} ${emp.last_name}` : '';
}
